import { useCallback } from 'react';
import { useAudioFiles } from './useAudioFiles';
import { useVoiceSynthesis } from './useVoiceSynthesis';

/**
 * Unified audio hook that uses MP3 files when available, falls back to TTS
 * This provides consistent, low-latency audio for the n-back game
 */
export const useGameAudio = () => {
  // MP3 file player
  const {
    loadingStatus,
    filesLoaded,
    totalFiles,
    playLetter: playLetterMP3,
    playVoiceId: playVoiceIdMP3,
    playCategoryWord: playCategoryWordMP3,
    playName: playNameMP3,
    hasMP3Files
  } = useAudioFiles();
  
  // TTS fallback
  const { voiceMap, playAudio: playAudioTTS } = useVoiceSynthesis();
  
  /**
   * Play letter audio
   * @param {string} letter - Single letter (C, H, K, L, Q, R, S, T)
   * @param {string} voice - Voice type (male1, male2, female1, female2)
   * @param {boolean} isVoiceOnly - If true, play voice ID instead of letter
   * @param {number} volumeMultiplier - Volume adjustment (0-2.3)
   */
  const playAudio = useCallback((letter, voice, isVoiceOnly = false, volumeMultiplier = 1.0) => {
    // Try MP3 files first
    if (hasMP3Files()) {
      let success = false;
      
      if (isVoiceOnly) {
        success = playVoiceIdMP3(voice, volumeMultiplier);
      } else {
        success = playLetterMP3(letter, voice, volumeMultiplier);
      }
      
      if (success) {
        return; // MP3 played successfully
      }
    }
    
    // Fallback to TTS if MP3 not available
    playAudioTTS(letter, voice, isVoiceOnly, volumeMultiplier);
  }, [hasMP3Files, playLetterMP3, playVoiceIdMP3, playAudioTTS]);
  
  /**
   * Play category word audio
   * @param {string} word - Category word (lowercase)
   * @param {string} voice - Voice type (male1, male2, female1, female2)
   * @param {number} volumeMultiplier - Volume adjustment
   */
  const playCategoryAudio = useCallback((word, voice = 'female2', volumeMultiplier = 1.0) => {
    // Try MP3 files first
    if (hasMP3Files()) {
      const success = playCategoryWordMP3(word, voice, volumeMultiplier);
      if (success) {
        return; // MP3 played successfully
      }
    }
    
    // Fallback to TTS
    playAudioTTS(word, voice, false, volumeMultiplier);
  }, [hasMP3Files, playCategoryWordMP3, playAudioTTS]);
  
  /**
   * Play name audio
   * @param {string} name - Name (lowercase)
   * @param {string} voice - Voice type
   * @param {number} volumeMultiplier - Volume adjustment
   */
  const playNameAudio = useCallback((name, voice = 'female2', volumeMultiplier = 1.0) => {
    // Try MP3 files first
    if (hasMP3Files()) {
      const success = playNameMP3(name, voice, volumeMultiplier);
      if (success) {
        return; // MP3 played successfully
      }
    }
    
    // Fallback to TTS
    playAudioTTS(name, voice, false, volumeMultiplier);
  }, [hasMP3Files, playNameMP3, playAudioTTS]);
  
  return {
    // Audio playback
    playAudio,
    playCategoryAudio,
    playNameAudio,
    
    // Loading status
    loadingStatus,
    filesLoaded,
    totalFiles,
    hasMP3Files: hasMP3Files(),
    
    // Voice info (for UI display)
    voiceMap
  };
};

